import type { AreaView } from '../../../services/areas/areas.service';
import type { OrderView } from '../../../services/orders/orders.service';
import { createRowKey } from '../../../utils/form-row-list';
import type {
  AreaFormState,
  EmployeeFormState,
  ProductFormRow,
  ProductFormState,
  TableFormState,
} from './pos-shell-page.types';

/**
 * Architectural summary: Initial / reset values for the POS shell forms.
 * Centralised so resetForm / resetAreaForm / resetTableForm / resetProductForm
 * cannot drift from the field shapes declared in pos-shell-page.types.ts. If a
 * new form field is added, update both the type and the matching INITIAL_*
 * constant in one place.
 */

export const INITIAL_FORM: EmployeeFormState = {
  fullName: '',
  pin: '',
  role: 'employee',
};

export const INITIAL_AREA_FORM: AreaFormState = {
  name: '',
};

export const INITIAL_TABLE_FORM: TableFormState = {
  number: '',
  shape: 'square',
};

export const INITIAL_PRODUCT_FORM: ProductFormState = {
  name: '',
  price: '',
  printerIds: [],
  kds: false,
  categoryId: null,
};

// Factory (not a shared constant): each call needs its own unique rowKey from
// utils/form-row-list.ts's createRowKey, so React keys stay stable per-row.
export const createProductFormRow = (): ProductFormRow => ({
  ...INITIAL_PRODUCT_FORM,
  imageFile: null,
  rowKey: createRowKey('product-row'),
});

/**
 * Architectural summary: Generic numeric clamp. Used by the floor-plan canvas
 * to keep dragged/resized tables inside their area bounds.
 */
export const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

/**
 * Architectural summary: Generic by-id merge for SSE delta handlers. Returns
 * a new array where the row matching `incoming.id` is replaced by `incoming`;
 * if no match exists the row is prepended (covers create events). Used by
 * every entity SSE handler in PosShellPage (areas, tables-within-area,
 * employees, products) so we apply only the changed row instead of refetching
 * the entire list. If you change ordering semantics here, every list view
 * that depends on stable order needs a re-sort step in its own handler.
 */
export function mergeById<T extends { id: number }>(list: T[], incoming: T): T[] {
  let found = false;
  const next = list.map((row) => {
    if (row.id === incoming.id) {
      found = true;
      return incoming;
    }
    return row;
  });
  if (!found) {
    next.unshift(incoming);
  }
  return next;
}

/**
 * Architectural summary: Removes a row by id. Used by `*.deactivated` SSE
 * handlers when the row should disappear from a list (e.g. inactive employees
 * from the picker, inactive products from the POS grid).
 */
export function removeById<T extends { id: number }>(list: T[], removedId: number): T[] {
  return list.filter((row) => row.id !== removedId);
}

/**
 * Architectural summary: Defensive normaliser for AreaView payloads — ensures
 * `tables` is always an array even if the backend omits it. Run on every
 * area entering local state from REST or SSE.
 */
export const normalizeArea = (area: AreaView): AreaView => ({
  ...area,
  tables: Array.isArray(area.tables) ? area.tables : [],
});

// A persistent (percentage) discount/tip blocks merging — only a manager may clear it.
export const hasPersistentMergeConflict = (order: OrderView | null | undefined): boolean =>
  order?.discountMode === 'percentage' || order?.tipInputMode === 'percent';

// Manager-controlled order (area-bar move-left/move-right), shared live across devices via sortIndex.
export const sortAreasForOperations = (areas: AreaView[]): AreaView[] =>
  [...areas].sort((first, second) => first.sortIndex - second.sortIndex);
